import React, { useLayoutEffect, useState } from 'react';

import { SORT_ORDER } from '@customTypes/Common';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

import Order from '@assets/Icons/Order';

import styles from './styles.module.scss';

const ROW_HEIGHT = 53;

export type ColumnDefinitionType<DataType, DataKeys extends keyof DataType> = {
  key: DataKeys;
  header: React.ReactNode;
  style?: React.CSSProperties;
  render?: (value: DataType) => React.ReactNode;
  sort?: (data: DataType[], key: DataKeys, order: SORT_ORDER) => DataType[];
};

export type TableProps<DataType, DataKeys extends keyof DataType> = {
  data: DataType[];
  columns: Array<ColumnDefinitionType<DataType, DataKeys>>;
  noDataComponent?: React.ReactNode;
};

const Table = <DataType extends Record<string, any>, DataKeys extends keyof DataType = keyof DataType>({
  data,
  columns,
  noDataComponent,
}: TableProps<DataType, DataKeys>): JSX.Element => {
  const [sortedData, setSortedData] = useState<DataType[]>([]);
  const [sortedBy, setSortedBy] = useState<DataKeys | null>(null);
  const [sortOrder, setSortOrder] = useState<SORT_ORDER | null>(null);

  const Row = ({
    index, // Index of row within collection
    style,
  }: any) => {
    return (
      <tr key={`row-${index}`} style={{ ...style }}>
        {columns.map((column, cIndex) => (
          <td className={sortOrder && sortedBy === column.key ? styles[sortOrder] : ''} key={`cell-${cIndex}`} style={column.style || {}}>
            <div className={styles.cell}>{column.render ? column.render(sortedData[index]) : sortedData[index][column.key]}</div>
          </td>
        ))}
        <td />
      </tr>
    );
  };

  const Inner = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLTableElement>>(function Inner({ children, ...rest }, ref) {
    return (
      <div {...rest} ref={ref}>
        <table>
          <thead>
            <tr>
              {columns.map(renderHeaderCell)}
              <td />
            </tr>
          </thead>
          <tbody style={{ position: 'relative', height: `calc(${ROW_HEIGHT}px * (${sortedData.length}))` }}>{children}</tbody>
        </table>
      </div>
    );
  });

  useLayoutEffect(() => {
    if (sortedBy && sortOrder) {
      const column = columns.find((c) => c.key === sortedBy);
      if (column?.sort) {
        setSortedData(column.sort(data, sortedBy, sortOrder));
        return;
      }
    }
    setSortedData([...data]);
  }, [columns, data, sortedBy, sortOrder]);

  const onClickHeaderCell = (column: ColumnDefinitionType<DataType, DataKeys>) => {
    if (column.sort) {
      setSortedBy(column.key);
      setSortOrder(sortOrder === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC);
    }
  };

  const renderHeaderCell = (column: ColumnDefinitionType<DataType, DataKeys>) => {
    const { key, header, style, sort } = column;
    return (
      <th className={sortedBy === column.key && sortOrder ? styles[sortOrder] : ''} key={String(key)} style={style || {}}>
        <div
          className={`${styles.header} ${sort ? styles.sortable : ''}`}
          onClick={() => {
            onClickHeaderCell(column);
          }}
        >
          {header}
          {sort && <Order size={16} />}
        </div>
      </th>
    );
  };
  return (
    <>
      {sortedData.length ? (
        <div className={styles.wrapper} style={{ height: `calc(${ROW_HEIGHT}px * (${sortedData.length} + 1))`, maxHeight: '100%' }}>
          <div className={styles.container}>
            <AutoSizer>
              {({ height, width }) => (
                <List width={width || 0} height={height || 0} itemCount={sortedData.length} itemSize={ROW_HEIGHT} innerElementType={Inner}>
                  {Row}
                </List>
              )}
            </AutoSizer>
          </div>
        </div>
      ) : (
        ''
      )}
      {noDataComponent && data.length === 0 && <div>{noDataComponent}</div>}
    </>
  );
};

export default Table;
